import { useState, useRef } from 'react'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { useBrands, useCreateBrand, useUploadImage, useUploadVideo, useSubscriptions, useUpdateBrand, useDeleteBrand } from '../hooks/useApi'
import { Building2, ExternalLink, Upload, Facebook, Instagram, Phone, X, Edit2, Check, AlertCircle, Film, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { LazadaIcon, ShopeeIcon, TokopediaIcon } from '../components/MarketplaceIcons'
import { CatalogSharePanel } from '../components/CatalogSharePanel'

const DEFAULT_THEME = '#3b82f6'
const DOMAIN = window.location.hostname === 'localhost' ? 'umkmindonesia.com' : window.location.hostname
const BASE_DOMAIN = import.meta.env.VITE_BASE_DOMAIN || 'umkmindonesia.my.id'
const THEME_OPTIONS = [
  { value: '#3b82f6', label: 'Default Biru' },
  { value: '#0f766e', label: 'Emerald' },
  { value: '#7c3aed', label: 'Royal Purple' },
  { value: '#ea580c', label: 'Sunset Orange' },
  { value: '#0f172a', label: 'Midnight Dark' },
  { value: '#db2777', label: 'Rose Pink' },
  { value: '#16a34a', label: 'Fresh Green' },
  { value: '#0891b2', label: 'Ocean Cyan' },
]

const THEME_PREVIEW: Record<string, { page: string; hero: string; accent: string; cta: string; text: string }> = {
  '#3b82f6': { page: '#f8f6f2', hero: '#ffd76a', accent: '#102a5c', cta: '#0f2b60', text: '#0f2b60' },
  '#0f766e': { page: '#f2f8f7', hero: '#a7f3d0', accent: '#047857', cta: '#064e3b', text: '#064e3b' },
  '#7c3aed': { page: '#f5f3ff', hero: '#ddd6fe', accent: '#6d28d9', cta: '#4c1d95', text: '#4c1d95' },
  '#ea580c': { page: '#fff7ed', hero: '#fed7aa', accent: '#c2410c', cta: '#9a3412', text: '#9a3412' },
  '#0f172a': { page: '#e2e8f0', hero: '#cbd5e1', accent: '#1e293b', cta: '#0f172a', text: '#0f172a' },
  '#db2777': { page: '#fff1f7', hero: '#fbcfe8', accent: '#be185d', cta: '#9d174d', text: '#9d174d' },
  '#16a34a': { page: '#f0fdf4', hero: '#bbf7d0', accent: '#15803d', cta: '#14532d', text: '#14532d' },
  '#0891b2': { page: '#ecfeff', hero: '#a5f3fc', accent: '#0e7490', cta: '#164e63', text: '#164e63' },
}

import { normalizePlanId, getPlanDisplayName, canUseFeature, resolveBrandLimit, formatPlanLimit, isUnderPlanLimit } from '../lib/plans'
import { getSubdomainCatalogUrl } from '../lib/subdomain'
import { getImageUrl } from '../lib/catalogUrl'

export default function Brand() {
  const { data: brands, isLoading } = useBrands()
  const { data: subscriptions } = useSubscriptions()
  const createBrand = useCreateBrand()
  const updateBrand = useUpdateBrand()
  const deleteBrand = useDeleteBrand()
  const uploadImage = useUploadImage()
  const uploadVideo = useUploadVideo()
  
  const currentSub = subscriptions?.[0]
  const currentPlan = normalizePlanId(currentSub?.isActive === true ? currentSub.plan : 'free')
  const currentPlanName = getPlanDisplayName(currentSub?.isActive === true ? currentSub.plan : 'free')
  const canCustomTheme = canUseFeature(currentPlan, 'custom_theme')
  const maxBrands = resolveBrandLimit(currentSub?.isActive === true ? currentSub?.brandLimit : undefined, currentPlan)
  const canCreateBrand = isUnderPlanLimit(brands?.length || 0, maxBrands)
  const maxBrandsLabel = formatPlanLimit(maxBrands)
  const primaryBrand = brands?.find((b: { parentBrandId?: number | null }) => !b.parentBrandId) ?? brands?.[0]
  const isAddingCategory = currentPlan === 'premium' && (brands?.length || 0) > 0
  
  // State for creating new brand
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    whatsapp: '',
    address: '',
    logoUrl: '',
    instagram: '',
    facebook: '',
    tiktok: '',
    tokopediaUrl: '',
    shopeeUrl: '',
    lazadaUrl: '',
    colorTheme: '#3b82f6',
    grabFoodUrl: '',
    goFoodUrl: '',
    shopeeFoodUrl: '',
    subdomain: '',
  })
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const logoFileInputRef = useRef<HTMLInputElement>(null)
  
  // State for editing brand
  const [editingBrand, setEditingBrand] = useState<any>(null)
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    whatsapp: '',
    address: '',
    logoUrl: '',
    instagram: '',
    facebook: '',
    tiktok: '',
    tokopediaUrl: '',
    shopeeUrl: '',
    lazadaUrl: '',
    colorTheme: '#3b82f6',
    grabFoodUrl: '',
    goFoodUrl: '',
    shopeeFoodUrl: '',
    promoBannerUrl: '',
    promoVideoUrl: '',
    subdomain: '',
  })
  const [isUploadingEditLogo, setIsUploadingEditLogo] = useState(false)
  const [isUploadingBanner, setIsUploadingBanner] = useState(false)
  const [isUploadingVideo, setIsUploadingVideo] = useState(false)
  const editLogoFileInputRef = useRef<HTMLInputElement>(null)
  const bannerFileInputRef = useRef<HTMLInputElement>(null)
  const videoFileInputRef = useRef<HTMLInputElement>(null)

  // Generate slug preview
  const slugPreview = formData.name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .substring(0, 50)

  const subdomainPreview = (formData.subdomain || slugPreview)
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canCreateBrand) {
      toast.error(`Paket ${currentPlanName} hanya mendukung ${maxBrandsLabel} katalog usaha.`)
      return
    }
    const payload: Record<string, unknown> = isAddingCategory
      ? { name: formData.name }
      : {
          ...formData,
          colorTheme: canCustomTheme ? formData.colorTheme : DEFAULT_THEME,
        }
    if (!canUseFeature(currentPlan, 'subdomain')) {
      delete payload.subdomain
    }
    await createBrand.mutateAsync(payload)
    setFormData({ name: '', description: '', whatsapp: '', address: '', logoUrl: '', instagram: '', facebook: '', tiktok: '', tokopediaUrl: '', shopeeUrl: '', lazadaUrl: '', colorTheme: '#3b82f6', grabFoodUrl: '', goFoodUrl: '', shopeeFoodUrl: '', subdomain: '' })
  }

  const handleDeleteBrand = async (brand: { id: number; name: string; parentBrandId?: number | null }) => {
    const isPrimary = !brand.parentBrandId
    const message = isPrimary
      ? `Hapus katalog utama "${brand.name}"? Semua produk di brand ini ikut terhapus.`
      : `Hapus kategori "${brand.name}"? Semua produk di kategori ini ikut terhapus.`
    if (!window.confirm(message)) return
    await deleteBrand.mutateAsync(brand.id)
  }
  
  const handleEditClick = (brand: any) => {
    setEditingBrand(brand)
    setEditFormData({
      name: brand.name || '',
      description: brand.description || '',
      whatsapp: brand.whatsapp || '',
      address: brand.address || '',
      logoUrl: brand.logoUrl || '',
      instagram: brand.instagram || '',
      facebook: brand.facebook || '',
      tiktok: brand.tiktok || '',
      tokopediaUrl: brand.tokopediaUrl || '',
      shopeeUrl: brand.shopeeUrl || '',
      lazadaUrl: brand.lazadaUrl || '',
      colorTheme: brand.colorTheme || '#3b82f6',
      grabFoodUrl: brand.grabFoodUrl || '',
      goFoodUrl: brand.goFoodUrl || '',
      shopeeFoodUrl: brand.shopeeFoodUrl || '',
      promoBannerUrl: brand.promoBannerUrl || '',
      promoVideoUrl: brand.promoVideoUrl || '',
      subdomain: brand.subdomain || '',
    })
  }
  
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingBrand) return
    
    await updateBrand.mutateAsync({
      id: editingBrand.id,
      data: editingBrand.parentBrandId
        ? { name: editFormData.name }
        : {
            ...editFormData,
            colorTheme: canCustomTheme ? editFormData.colorTheme : DEFAULT_THEME,
          },
    })
    setEditingBrand(null)
  }
  
  const handleEditLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    setIsUploadingEditLogo(true)
    try {
      const result = await uploadImage.mutateAsync(file)
      setEditFormData({...editFormData, logoUrl: result.url})
    } catch (error) {
      console.error('Logo upload error:', error)
    } finally {
      setIsUploadingEditLogo(false)
      if (editLogoFileInputRef.current) {
        editLogoFileInputRef.current.value = ''
      }
    }
  }

  if (isLoading) return <div>Loading...</div>

  const renderThemePreview = (colorTheme: string) => {
    const theme = THEME_PREVIEW[colorTheme] || THEME_PREVIEW['#3b82f6']
    return (
      <div className="rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-3 py-2 text-xs font-semibold bg-gray-50 text-gray-700">
          Preview Landing Page
        </div>
        <div className="p-3 space-y-2" style={{ backgroundColor: theme.page }}>
          <div className="h-8 rounded-md border" style={{ borderColor: `${theme.accent}40`, backgroundColor: '#ffffff' }} />
          <div className="h-14 rounded-md border" style={{ borderColor: `${theme.accent}55`, backgroundColor: theme.hero }} />
          <div className="flex items-center justify-between gap-2">
            <div className="h-7 w-2/5 rounded-md" style={{ backgroundColor: theme.accent }} />
            <div className="h-7 w-1/3 rounded-md" style={{ backgroundColor: '#25D366' }} />
          </div>
          <div className="h-8 rounded-md" style={{ backgroundColor: theme.cta }} />
          <p className="text-[11px] font-medium" style={{ color: theme.text }}>
            Warna utama akan mengikuti tema ini pada landing page.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Kelola Brand/Toko</h1>
      {isAddingCategory && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          Paket Premium: brand tambahan akan tampil sebagai <strong>kategori</strong> dalam satu katalog (
          {primaryBrand?.name}). Link & subdomain tetap mengarah ke katalog utama.
        </p>
      )}
      
      {/* Plan Info */}
      <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm sm:text-base">
        <span className="text-gray-600">Paket:</span>
        <span className="font-semibold text-blue-600">{currentPlanName}</span>
        <span className="hidden sm:inline text-gray-400">|</span>
        <span className="text-gray-600">Brand:</span>
        <span className={`font-semibold ${!canCreateBrand ? 'text-red-600' : 'text-green-600'}`}>
          {brands?.length || 0} / {maxBrandsLabel}
        </span>
        {(Number.isFinite(maxBrands) && (brands?.length || 0) >= maxBrands) && (
          <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded inline-flex items-center gap-1 w-full sm:w-auto">
            <AlertCircle className="w-3 h-3 shrink-0" />
            Limit tercapai
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Create Brand Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {isAddingCategory ? 'Tambah Kategori Brand' : 'Buat Brand Baru'}
              {!canCreateBrand && (
                <span className="text-xs text-red-500 font-normal">(Limit tercapai)</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!canCreateBrand && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Paket {currentPlanName} hanya bisa membuat {maxBrandsLabel} brand. 
                  <a href="/subscription" className="text-blue-600 underline">Upgrade ke Premium</a> untuk katalog tanpa batas!
                </p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4 opacity-100">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isAddingCategory ? 'Nama Kategori Brand *' : 'Nama Brand/Toko *'}
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder={isAddingCategory ? 'Contoh: Minuman, Snack, Fashion' : 'Contoh: Ayam Geprek Pak Slamet'}
                  required
                />
              </div>
              
              {/* Slug Preview — hanya untuk katalog utama */}
              {formData.name && !isAddingCategory && (
                <div className="p-3 bg-blue-50 rounded-lg overflow-hidden space-y-2">
                  <div>
                    <p className="text-sm text-blue-700 font-medium">URL Katalog:</p>
                    <p className="text-sm text-blue-600 break-all">{DOMAIN}/catalog/{slugPreview}</p>
                    <p className="text-xs text-blue-500 mt-1">Slug otomatis dibuat dari nama brand</p>
                  </div>
                  {canUseFeature(currentPlan, 'subdomain') && (
                    <div className="pt-2 border-t border-blue-200">
                      <p className="text-sm text-amber-800 font-medium">Subdomain Premium:</p>
                      <p className="text-sm text-amber-700 break-all">
                        {subdomainPreview ? `${subdomainPreview}.${BASE_DOMAIN}` : `—.${BASE_DOMAIN}`}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {isAddingCategory && formData.name && (
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-sm text-emerald-800 font-medium">Akan tampil di katalog:</p>
                  <p className="text-sm text-emerald-700 break-all mt-1">
                    {DOMAIN}/catalog/{primaryBrand?.slug}#kategori-{slugPreview || 'nama-kategori'}
                  </p>
                  <p className="text-xs text-emerald-600 mt-1">
                    Kategori ini bagian dari katalog utama, bukan link terpisah.
                  </p>
                </div>
              )}

              {canUseFeature(currentPlan, 'subdomain') && !isAddingCategory && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4 space-y-2">
                  <label className="block text-sm font-medium text-amber-900">
                    Subdomain Katalog Premium
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={formData.subdomain}
                      onChange={(e) => setFormData({ ...formData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      placeholder={slugPreview || 'namatoko'}
                      maxLength={50}
                    />
                    <span className="text-sm text-amber-800 shrink-0">.{BASE_DOMAIN}</span>
                  </div>
                  <p className="text-xs text-amber-800">
                    Kosongkan untuk memakai slug otomatis. Minimal 3 karakter (huruf, angka, strip).
                  </p>
                </div>
              )}
              
              {isAddingCategory && (
                <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                  Logo, tema, deskripsi, dan kontak mengikuti katalog utama ({primaryBrand?.name}).
                  Cukup isi nama kategori — tampilan landing page tetap seragam.
                </p>
              )}

              {!isAddingCategory && (
              <>
              {/* Logo Upload — katalog utama */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Logo Toko (opsional)
                </label>
                
                <input
                  type="file"
                  ref={logoFileInputRef}
                  onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    
                    setIsUploadingLogo(true)
                    try {
                      const result = await uploadImage.mutateAsync(file)
                      setFormData({...formData, logoUrl: result.url})
                    } catch (error) {
                      console.error('Logo upload error:', error)
                    } finally {
                      setIsUploadingLogo(false)
                      if (logoFileInputRef.current) {
                        logoFileInputRef.current.value = ''
                      }
                    }
                  }}
                  accept="image/*"
                  className="hidden"
                />
                
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  {formData.logoUrl ? (
                    <div className="relative">
                      <img 
                        src={getImageUrl(formData.logoUrl) || ''} 
                        alt="Logo preview"
                        className="w-16 h-16 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, logoUrl: ''})}
                        className="absolute -top-1 -right-1 bg-red-500 text-white w-5 h-5 rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => logoFileInputRef.current?.click()}
                      disabled={isUploadingLogo}
                      className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-blue-500 hover:text-blue-500 transition-colors disabled:opacity-50"
                    >
                      {isUploadingLogo ? (
                        <span className="text-xs">...</span>
                      ) : (
                        <Upload className="w-6 h-6" />
                      )}
                    </button>
                  )}
                  <p className="text-xs text-gray-500">
                    Upload logo toko Anda. Max 5MB.
                  </p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deskripsi
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Ceritakan tentang brand/toko Anda"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nomor WhatsApp
                </label>
                <Input
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                  placeholder="6281234567890"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alamat Toko
                </label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="Jl. Mawar No. 123, Jakarta"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Theme Landing Page
                  {canCustomTheme && (
                    <span className="ml-2 text-xs font-normal text-indigo-600">(Paket Basic & Premium)</span>
                  )}
                </label>
                {canCustomTheme ? (
                  <>
                    <select
                      value={formData.colorTheme}
                      onChange={(e) => setFormData({ ...formData, colorTheme: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {THEME_OPTIONS.map((theme) => (
                        <option key={theme.value} value={theme.value}>
                          {theme.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Pilih warna tema katalog. Tersedia untuk paket Basic dan Premium.
                    </p>
                    <div className="mt-3">
                      {renderThemePreview(formData.colorTheme)}
                    </div>
                  </>
                ) : (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
                    <p className="text-sm text-gray-700">Paket Gratis memakai <strong>tema default biru</strong> saja.</p>
                    <div className="mt-2">{renderThemePreview(DEFAULT_THEME)}</div>
                    <p className="text-xs text-amber-700">
                      Upgrade ke <a href="/subscription" className="underline font-medium">Basic</a> untuk kustomisasi warna katalog.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Media Sosial (opsional)</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Instagram className="w-5 h-5 text-pink-600 shrink-0" />
                    <Input
                      className="min-w-0 flex-1"
                      value={formData.instagram}
                      onChange={(e) => setFormData({...formData, instagram: e.target.value})}
                      placeholder="@username"
                    />
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <Facebook className="w-5 h-5 text-blue-600 shrink-0" />
                    <Input
                      className="min-w-0 flex-1"
                      value={formData.facebook}
                      onChange={(e) => setFormData({...formData, facebook: e.target.value})}
                      placeholder="username"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-1">Toko online marketplace (opsional)</h4>
                <p className="text-xs text-gray-500 mb-3">Tampil di katalog publik. Tempel URL lengkap halaman toko Anda.</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-green-700 mb-1">Tokopedia</label>
                    <Input
                      value={formData.tokopediaUrl}
                      onChange={(e) => setFormData({ ...formData, tokopediaUrl: e.target.value })}
                      placeholder="https://www.tokopedia.com/..."
                      type="url"
                      inputMode="url"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-orange-600 mb-1">Shopee</label>
                    <Input
                      value={formData.shopeeUrl}
                      onChange={(e) => setFormData({ ...formData, shopeeUrl: e.target.value })}
                      placeholder="https://shopee.co.id/..."
                      type="url"
                      inputMode="url"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-blue-700 mb-1">Lazada</label>
                    <Input
                      value={formData.lazadaUrl}
                      onChange={(e) => setFormData({ ...formData, lazadaUrl: e.target.value })}
                      placeholder="https://www.lazada.co.id/..."
                      type="url"
                      inputMode="url"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Food Delivery (opsional)</h4>
                  <p className="text-xs text-gray-500 mt-1">
                    Salin link toko dari aplikasi (menu Bagikan / Share). Pelanggan dibuka langsung ke toko Anda di GrabFood, GoFood, atau ShopeeFood.
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-green-700 mb-1">GrabFood — link toko</label>
                  <Input
                    value={formData.grabFoodUrl}
                    onChange={(e) => setFormData({ ...formData, grabFoodUrl: e.target.value })}
                    placeholder="https://food.grab.com/id/..."
                    type="url"
                    inputMode="url"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-green-700 mb-1">GoFood — link toko</label>
                  <Input
                    value={formData.goFoodUrl}
                    onChange={(e) => setFormData({ ...formData, goFoodUrl: e.target.value })}
                    placeholder="https://gofood.co.id/..."
                    type="url"
                    inputMode="url"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-orange-700 mb-1">ShopeeFood — link toko</label>
                  <Input
                    value={formData.shopeeFoodUrl}
                    onChange={(e) => setFormData({ ...formData, shopeeFoodUrl: e.target.value })}
                    placeholder="https://shopeefood.co.id/..."
                    type="url"
                    inputMode="url"
                  />
                </div>
              </div>
              </>
              )}
              
              <Button type="submit" className="w-full" disabled={!canCreateBrand || createBrand.isPending}>
                {createBrand.isPending ? 'Menyimpan...' : isAddingCategory ? 'Simpan Kategori' : 'Simpan Brand'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Brand List */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Brand Anda</h2>
          
          {/* Edit Brand Modal */}
          {editingBrand && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Edit2 className="w-5 h-5" />
                    {editingBrand.parentBrandId ? 'Edit Kategori' : 'Edit Brand'}: {editingBrand.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleEditSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {editingBrand.parentBrandId ? 'Nama Kategori *' : 'Nama Brand/Toko *'}
                      </label>
                      <Input
                        value={editFormData.name}
                        onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                        placeholder={editingBrand.parentBrandId ? 'Contoh: Minuman, Snack' : 'Contoh: Ayam Geprek Pak Slamet'}
                        required
                      />
                    </div>

                    {editingBrand.parentBrandId ? (
                      <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                        Logo, tema, dan deskripsi mengikuti katalog utama. Hanya nama kategori yang dapat diubah.
                      </p>
                    ) : (
                    <>
                    {/* Logo Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Logo Toko
                      </label>
                      <input
                        type="file"
                        ref={editLogoFileInputRef}
                        onChange={handleEditLogoUpload}
                        accept="image/*"
                        className="hidden"
                      />
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        {editFormData.logoUrl ? (
                          <div className="relative">
                            <img 
                              src={getImageUrl(editFormData.logoUrl) || ''} 
                              alt="Logo preview"
                              className="w-16 h-16 object-cover rounded-lg border"
                            />
                            <button
                              type="button"
                              onClick={() => setEditFormData({...editFormData, logoUrl: ''})}
                              className="absolute -top-1 -right-1 bg-red-500 text-white w-5 h-5 rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => editLogoFileInputRef.current?.click()}
                            disabled={isUploadingEditLogo}
                            className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-blue-500 hover:text-blue-500 transition-colors disabled:opacity-50"
                          >
                            {isUploadingEditLogo ? (
                              <span className="text-xs">...</span>
                            ) : (
                              <Upload className="w-6 h-6" />
                            )}
                          </button>
                        )}
                        <p className="text-xs text-gray-500">
                          Upload logo toko. Max 5MB.
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Deskripsi
                      </label>
                      <textarea
                        value={editFormData.description}
                        onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                        placeholder="Ceritakan tentang brand/toko Anda"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={3}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nomor WhatsApp
                      </label>
                      <Input
                        value={editFormData.whatsapp}
                        onChange={(e) => setEditFormData({...editFormData, whatsapp: e.target.value})}
                        placeholder="6281234567890"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Alamat Toko
                      </label>
                      <Input
                        value={editFormData.address}
                        onChange={(e) => setEditFormData({...editFormData, address: e.target.value})}
                        placeholder="Jl. Mawar No. 123, Jakarta"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Theme Landing Page
                        {canCustomTheme && (
                          <span className="ml-2 text-xs font-normal text-indigo-600">(Paket Basic & Premium)</span>
                        )}
                      </label>
                      {canCustomTheme ? (
                        <>
                          <select
                            value={editFormData.colorTheme}
                            onChange={(e) => setEditFormData({ ...editFormData, colorTheme: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {THEME_OPTIONS.map((theme) => (
                              <option key={theme.value} value={theme.value}>
                                {theme.label}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Pilih warna tema katalog. Tersedia untuk paket Basic dan Premium.
                          </p>
                          <div className="mt-3">
                            {renderThemePreview(editFormData.colorTheme)}
                          </div>
                        </>
                      ) : (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
                          <p className="text-sm text-gray-700">Paket Gratis memakai tema default biru.</p>
                          <div className="mt-2">{renderThemePreview(DEFAULT_THEME)}</div>
                          <p className="text-xs text-amber-700">
                            <a href="/subscription" className="underline font-medium">Upgrade ke Basic</a> untuk ubah warna katalog.
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Media Sosial</h4>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <Instagram className="w-5 h-5 text-pink-600 shrink-0" />
                          <Input
                            className="min-w-0 flex-1"
                            value={editFormData.instagram}
                            onChange={(e) => setEditFormData({...editFormData, instagram: e.target.value})}
                            placeholder="@username"
                          />
                        </div>
                        <div className="flex items-center gap-2 min-w-0">
                          <Facebook className="w-5 h-5 text-blue-600 shrink-0" />
                          <Input
                            className="min-w-0 flex-1"
                            value={editFormData.facebook}
                            onChange={(e) => setEditFormData({...editFormData, facebook: e.target.value})}
                            placeholder="username"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Toko online marketplace (opsional)</h4>
                      <p className="text-xs text-gray-500 mb-3">URL lengkap halaman toko di marketplace.</p>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-green-700 mb-1">Tokopedia</label>
                          <Input
                            value={editFormData.tokopediaUrl}
                            onChange={(e) => setEditFormData({ ...editFormData, tokopediaUrl: e.target.value })}
                            placeholder="https://www.tokopedia.com/..."
                            type="url"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-orange-600 mb-1">Shopee</label>
                          <Input
                            value={editFormData.shopeeUrl}
                            onChange={(e) => setEditFormData({ ...editFormData, shopeeUrl: e.target.value })}
                            placeholder="https://shopee.co.id/..."
                            type="url"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-blue-700 mb-1">Lazada</label>
                          <Input
                            value={editFormData.lazadaUrl}
                            onChange={(e) => setEditFormData({ ...editFormData, lazadaUrl: e.target.value })}
                            placeholder="https://www.lazada.co.id/..."
                            type="url"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-4 space-y-3">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700">Food Delivery (opsional)</h4>
                        <p className="text-xs text-gray-500 mt-1">
                          Link toko dari GrabFood / GoFood / ShopeeFood (fitur Bagikan di aplikasi merchant).
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-green-700 mb-1">GrabFood</label>
                        <Input
                          value={editFormData.grabFoodUrl}
                          onChange={(e) => setEditFormData({ ...editFormData, grabFoodUrl: e.target.value })}
                          placeholder="https://food.grab.com/id/..."
                          type="url"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-green-700 mb-1">GoFood</label>
                        <Input
                          value={editFormData.goFoodUrl}
                          onChange={(e) => setEditFormData({ ...editFormData, goFoodUrl: e.target.value })}
                          placeholder="https://gofood.co.id/..."
                          type="url"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-orange-700 mb-1">ShopeeFood</label>
                        <Input
                          value={editFormData.shopeeFoodUrl}
                          onChange={(e) => setEditFormData({ ...editFormData, shopeeFoodUrl: e.target.value })}
                          placeholder="https://shopeefood.co.id/..."
                          type="url"
                        />
                      </div>
                    </div>

                    {canUseFeature(currentPlan, 'subdomain') && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4 space-y-2">
                        <label className="block text-sm font-medium text-amber-900">
                          Subdomain Katalog Premium
                        </label>
                        <div className="flex items-center gap-2">
                          <Input
                            value={editFormData.subdomain}
                            onChange={(e) => setEditFormData({ ...editFormData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                            placeholder={editingBrand?.slug || 'namatoko'}
                            maxLength={50}
                          />
                          <span className="text-sm text-amber-800 shrink-0">.{BASE_DOMAIN}</span>
                        </div>
                        <p className="text-xs text-amber-800">
                          Katalog dapat diakses via{' '}
                          <strong>
                            {(editFormData.subdomain || editingBrand?.slug || 'namatoko')}.{BASE_DOMAIN}
                          </strong>
                        </p>
                      </div>
                    )}

                    {canUseFeature(currentPlan, 'promo_banner') && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4 space-y-3">
                        <label className="block text-sm font-medium text-amber-900">
                          Banner Promosi Katalog (Premium)
                        </label>

                        <input
                          type="file"
                          ref={bannerFileInputRef}
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0]
                            if (!file) return
                            setIsUploadingBanner(true)
                            try {
                              const result = await uploadImage.mutateAsync(file)
                              setEditFormData({ ...editFormData, promoBannerUrl: result.url })
                            } catch (error) {
                              console.error('Banner upload error:', error)
                            } finally {
                              setIsUploadingBanner(false)
                              if (bannerFileInputRef.current) bannerFileInputRef.current.value = ''
                            }
                          }}
                        />

                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                          {editFormData.promoBannerUrl ? (
                            <div className="relative">
                              <img
                                src={getImageUrl(editFormData.promoBannerUrl) || ''}
                                alt="Banner preview"
                                className="w-full max-w-xs h-24 object-cover rounded-lg border"
                              />
                              <button
                                type="button"
                                onClick={() => setEditFormData({ ...editFormData, promoBannerUrl: '' })}
                                className="absolute -top-1 -right-1 bg-red-500 text-white w-5 h-5 rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : null}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => bannerFileInputRef.current?.click()}
                            disabled={isUploadingBanner}
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            {isUploadingBanner ? 'Mengunggah...' : 'Upload Banner'}
                          </Button>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-amber-800 mb-1">
                            Atau URL gambar (opsional)
                          </label>
                          <Input
                            value={editFormData.promoBannerUrl}
                            onChange={(e) => setEditFormData({ ...editFormData, promoBannerUrl: e.target.value })}
                            placeholder="https://..."
                            type="url"
                          />
                        </div>
                        <p className="text-xs text-amber-800">
                          Banner akan tampil di halaman katalog publik Anda.
                        </p>

                        <div className="pt-3 border-t border-amber-200 space-y-3">
                          <label className="block text-sm font-medium text-amber-900">
                            Video Promosi (opsional)
                          </label>

                          <input
                            type="file"
                            ref={videoFileInputRef}
                            accept="video/mp4,video/webm,video/quicktime"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0]
                              if (!file) return
                              setIsUploadingVideo(true)
                              try {
                                const result = await uploadVideo.mutateAsync(file)
                                setEditFormData({ ...editFormData, promoVideoUrl: result.url })
                              } catch (error) {
                                console.error('Video upload error:', error)
                              } finally {
                                setIsUploadingVideo(false)
                                if (videoFileInputRef.current) videoFileInputRef.current.value = ''
                              }
                            }}
                          />

                          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            {editFormData.promoVideoUrl ? (
                              <div className="relative w-full max-w-xs">
                                <video
                                  src={getImageUrl(editFormData.promoVideoUrl) || ''}
                                  className="w-full rounded-lg border max-h-32"
                                  controls
                                />
                                <button
                                  type="button"
                                  onClick={() => setEditFormData({ ...editFormData, promoVideoUrl: '' })}
                                  className="absolute -top-1 -right-1 bg-red-500 text-white w-5 h-5 rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : null}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => videoFileInputRef.current?.click()}
                              disabled={isUploadingVideo}
                            >
                              <Film className="w-4 h-4 mr-2" />
                              {isUploadingVideo ? 'Mengunggah...' : 'Upload Video'}
                            </Button>
                          </div>
                          <p className="text-xs text-amber-800">
                            Format mp4, webm, atau mov. Maks. 50MB.
                          </p>
                        </div>
                      </div>
                    )}

                    </>
                    )}

                    <div className="flex flex-col-reverse sm:flex-row gap-2 pt-4">
                      <Button 
                        type="button" 
                        variant="outline" 
                        className="sm:flex-initial"
                        onClick={() => setEditingBrand(null)}
                        disabled={updateBrand.isPending}
                      >
                        Batal
                      </Button>
                      <Button type="submit" className="flex-1 w-full sm:w-auto" disabled={updateBrand.isPending}>
                        <Check className="w-4 h-4 mr-2" />
                        {updateBrand.isPending ? 'Menyimpan...' : 'Simpan Perubahan'}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}
          
          {brands?.map((brand: any) => {
            const isCategory = Boolean(brand.parentBrandId)
            const catalogSlug = primaryBrand?.slug || brand.slug
            return (
            <Card key={brand.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  {(brand.logoUrl || (isCategory && primaryBrand?.logoUrl)) ? (
                    <img 
                      src={getImageUrl(brand.logoUrl || primaryBrand?.logoUrl) || ''} 
                      alt={brand.name}
                      className="w-16 h-16 rounded-lg object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center shrink-0">
                      <Building2 className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-lg break-words">{brand.name}</h3>
                        <p className="text-sm text-gray-500">
                          {isCategory ? (
                            <span className="inline-flex items-center gap-1">
                              <span className="text-emerald-700 font-medium">Kategori</span>
                              <span>· @{brand.slug}</span>
                            </span>
                          ) : (
                            <>Katalog utama · @{brand.slug}</>
                          )}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2 shrink-0 self-start sm:self-auto">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEditClick(brand)}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          onClick={() => handleDeleteBrand(brand)}
                          disabled={deleteBrand.isPending}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Hapus
                        </Button>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 line-clamp-2 break-words">{brand.description}</p>
                    
                    <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-gray-500">
                      {brand.whatsapp && <Phone className="w-4 h-4 shrink-0" />}
                      <span className="break-all">{brand.whatsapp}</span>
                    </div>
                    
                    {/* Catalog Link */}
                    {!isCategory ? (
                      <>
                        <a 
                          href={`/catalog/${catalogSlug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-start gap-1 mt-3 text-sm text-blue-600 hover:text-blue-800 break-all"
                        >
                          <ExternalLink className="w-4 h-4 shrink-0 mt-0.5" />
                          <span>Lihat Katalog: {DOMAIN}/catalog/{catalogSlug}</span>
                        </a>
                        {canUseFeature(currentPlan, 'subdomain') && (brand.subdomain || brand.slug) && (
                          <a
                            href={getSubdomainCatalogUrl(brand.subdomain || brand.slug)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-start gap-1 mt-1 text-sm text-amber-700 hover:text-amber-900 break-all"
                          >
                            <ExternalLink className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>Subdomain Premium: {(brand.subdomain || brand.slug)}.{BASE_DOMAIN}</span>
                          </a>
                        )}
                        <CatalogSharePanel brandName={brand.name} slug={brand.slug} />
                      </>
                    ) : (
                      <a
                        href={`/catalog/${catalogSlug}#kategori-${brand.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-start gap-1 mt-3 text-sm text-emerald-700 hover:text-emerald-900 break-all"
                      >
                        <ExternalLink className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>Lihat di katalog: #{brand.slug}</span>
                      </a>
                    )}
                    {(brand.tokopediaUrl || brand.shopeeUrl || brand.lazadaUrl) && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {brand.tokopediaUrl && (
                          <a
                            href={brand.tokopediaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Tokopedia"
                            aria-label="Buka Tokopedia"
                            className="inline-flex size-9 items-center justify-center rounded-lg border border-green-200 bg-white p-0.5 shadow-sm hover:bg-green-50"
                          >
                            <TokopediaIcon className="size-8" />
                          </a>
                        )}
                        {brand.shopeeUrl && (
                          <a
                            href={brand.shopeeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Shopee"
                            aria-label="Buka Shopee"
                            className="inline-flex size-9 items-center justify-center rounded-lg border border-orange-200 bg-white p-0.5 shadow-sm hover:bg-orange-50"
                          >
                            <ShopeeIcon className="size-8" />
                          </a>
                        )}
                        {brand.lazadaUrl && (
                          <a
                            href={brand.lazadaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Lazada"
                            aria-label="Buka Lazada"
                            className="inline-flex size-9 items-center justify-center rounded-lg border border-blue-200 bg-white p-0.5 shadow-sm hover:bg-blue-50"
                          >
                            <LazadaIcon className="size-8" />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            )
          })}
          {brands?.length === 0 && (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Belum ada brand. Buat brand pertama Anda!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
